/**
 * studentConversationAdapter — la conversation de l'élève avec un tuteur, du compte connecté et de
 * la messagerie (`api/messenger/threads/{id}/…`) → ce que `<StudentTutorConversationPage>` montre.
 *
 * Port de l'écran legacy `common/messenger/ChatScreen.js` (quel fil, quel tuteur — branche élève),
 * de son en-tête `ChatScreenHeader.js` et de `CommonChat.transformMessage`, sur les mêmes champs.
 */
import { decode } from "html-entities"

import { toUITutor, type ApiLiveOccurrenceTutor } from "../../parent/LiveSessions/liveSessionListAdapter"
import { finishedActivityStatus } from "../Activity/finishedActivityAdapter"
import { type UIActivityStatus, type UITutor } from "../../../ui/types"
import { type UIConversationMessage, type UIMessageAttachment } from "../../../ui/types/studentConversation"

/**
 * Un message tel que la messagerie le sert (`RTippin\Messenger\Http\Resources\MessageResource`).
 * Les chemins des pièces sont relatifs à l'API ; une pièce absente est une clé absente.
 */
export type ApiMessengerMessage = {
    id?: string | number
    thread_id?: string | number
    owner_id?: string | number
    body?: string | null
    created_at?: string | null
    image?: { sm?: string; md?: string; lg?: string } | null
    audio?: string | null
    document?: string | null
}

/** Une séance du compte (`UserResource.live_sessions`) : son tuteur et les fils privés par interlocuteur. */
export type ApiConversationLiveSession = {
    tutor?: ApiLiveOccurrenceTutor | null
    /** Par interlocuteur (l'id du tuteur, pour un élève) : l'id du fil privé avec lui. */
    thread_ids?: Record<string, string | number | null | undefined> | null
}

/** Forme lâche : le compte vient d'un réducteur en JS, tous les champs sont optionnels. */
export type ApiConversationUser = {
    id?: string | number
    tutor?: ApiLiveOccurrenceTutor | null
    /** Le fil avec le tuteur principal (la route `messenger` sans `tutorId`). */
    tutor_messenger_thread_id?: string | number | null
    live_sessions?: ApiConversationLiveSession[] | null
    /** Les messages lus, par fil — remplis par `getMessages` / `getPageMessages` (`currentUser.js`). */
    messages?: Record<string, unknown> | null
    /** Les activités du compte : celles que le tuteur a tirées d'un message portent son id. */
    todo_activities?: ApiChatActivity[] | null
    activities?: ApiChatActivity[] | null
}

/** Une activité du compte, ce que le fil en lit : le message d'où elle vient, et où elle en est. */
export type ApiChatActivity = {
    id?: string | number | null
    message_id?: string | number | null
    status?: string | null
}

/** Le statut de l'activité tirée d'un message, par id de message. */
export type ActivityStatusOf = (messageId: string) => UIActivityStatus | undefined

/**
 * Les activités tirées d'un message du fil — la pastille de `CommonChat.renderMessageImage` et
 * `renderMessagePdf` : les activités à faire puis les autres (`todo_activities.concat(activities)`),
 * la première qui nomme le message. Le statut se lit par la règle de la `DoneChip`, la même que la
 * page d'une activité faite (`finishedActivityStatus`).
 */
export function chatActivityStatuses(user: ApiConversationUser | null | undefined): ActivityStatusOf {
    return chatActivities(user).statusOf
}

/**
 * Le même parcours, une fois : le statut de l'activité tirée d'un message (la pastille) et son id (ce que
 * le tuteur ouvre en touchant la pièce, `CommonChat`, branche tuteur).
 */
export function chatActivities(user: ApiConversationUser | null | undefined): {
    statusOf: ActivityStatusOf
    activityIdByMessage: Map<string, string>
} {
    const statuses = new Map<string, UIActivityStatus>()
    const activityIdByMessage = new Map<string, string>()
    for (const activity of [...(user?.todo_activities ?? []), ...(user?.activities ?? [])]) {
        const messageId = idOf(activity?.message_id)
        if (messageId == null || statuses.has(messageId)) continue
        statuses.set(messageId, finishedActivityStatus(activity.status))
        const activityId = idOf(activity?.id)
        if (activityId != null) activityIdByMessage.set(messageId, activityId)
    }
    return { statusOf: (messageId) => statuses.get(messageId), activityIdByMessage }
}

export type TutorConversationTarget = {
    /** Le tuteur, `null` quand le compte ne le connaît pas (un lien vers un tuteur qui n'est plus le sien). */
    tutor: UITutor | null
    /** `null` : aucun fil connu avec ce tuteur. */
    threadId: string | null
}

/** Un id de la messagerie en chaîne ; vide ou absent : `null`. */
export const idOf = (value: string | number | null | undefined): string | null =>
    value == null || value === "" ? null : String(value)

/**
 * Le fil que les séances nomment pour un interlocuteur (`live_sessions[].thread_ids`, fusionnés dans
 * l'ordre des séances : la dernière qui le nomme l'emporte, même nul) — lu de la dernière à la première,
 * sans recopier les fusions.
 */
export function sessionThreadId(
    sessions: ({ thread_ids?: Record<string, string | number | null | undefined> | null } | null)[] | null | undefined,
    interlocutorId: string
): string | null {
    const list = sessions ?? []
    for (let index = list.length - 1; index >= 0; index -= 1) {
        const threadIds = list[index]?.thread_ids
        if (threadIds != null && Object.prototype.hasOwnProperty.call(threadIds, interlocutorId)) {
            return idOf(threadIds[interlocutorId])
        }
    }
    return null
}

/**
 * Le `tutorId` qui ouvre la conversation d'un tuteur : aucun pour le tuteur principal (sa
 * conversation est celle de la route `messenger` sans paramètre), le sien pour les autres.
 */
export function conversationTutorId<Id extends string | number>(
    tutorId: Id,
    mainTutorId: string | number | null | undefined
): Id | null {
    return String(tutorId) === String(mainTutorId) ? null : tutorId
}

/**
 * Le tuteur et le fil d'une conversation — la branche élève de `ChatScreen.js:57-69` et de
 * `ChatScreenHeader.js:25` :
 *  - sans `tutorId` : le tuteur principal (`tutor`) et son fil (`tutor_messenger_thread_id`) ;
 *  - avec `tutorId` : le tuteur d'une séance (`live_sessions[].tutor`) et le fil que les séances
 *    nomment pour lui (`live_sessions[].thread_ids`, fusionnés dans l'ordre des séances).
 *
 * Écart voulu : un `tutorId` qui est celui du tuteur principal (la notification d'un message
 * l'envoie toujours) retombe sur le principal quand aucune séance ne le nomme — le legacy ouvrait
 * alors une conversation sans fil ni en-tête. C'est le même fil : la messagerie n'a qu'un fil privé
 * par paire (`LiveSession::createThreadIdsForUser`, `User::getTutorMessengerThreadIdAttribute`).
 */
export function tutorConversationTarget(
    user: ApiConversationUser | null | undefined,
    tutorId: string | number | null | undefined
): TutorConversationTarget {
    const mainTutor = user?.tutor?.id != null ? user.tutor : null
    const mainThreadId = idOf(user?.tutor_messenger_thread_id)
    const wanted = idOf(tutorId)

    if (wanted == null) {
        return { tutor: mainTutor != null ? toUITutor(mainTutor) : null, threadId: mainThreadId }
    }

    const sessions = user?.live_sessions ?? []
    const sessionTutor = sessions.find((session) => idOf(session?.tutor?.id) === wanted)?.tutor ?? null
    const isMain = mainTutor != null && idOf(mainTutor.id) === wanted

    const tutor = sessionTutor ?? (isMain ? mainTutor : null)
    const threadId = sessionThreadId(sessions, wanted) ?? (isMain ? mainThreadId : null)
    return { tutor: tutor != null ? toUITutor(tutor) : null, threadId }
}

/** `CommonChat.cleanURLsInBody` : les adresses d'un message sont encodées pour rester cliquables. */
export function cleanUrlsInBody(body: string): string {
    const urls = body.match(/(https?:\/\/[^\s]+)/g) ?? []
    let cleaned = body
    for (const url of urls) {
        cleaned = cleaned.replace(url, encodeURI(url).replace(/%25/g, "%"))
    }
    return cleaned
}

/** Le dernier segment du chemin d'un document : le nom que le legacy écrivait sur sa bulle. */
function documentName(path: string): string | undefined {
    const name = path.split("?")[0]?.split("/").pop()
    return name != null && name !== "" ? name : undefined
}

function attachmentOf(
    raw: ApiMessengerMessage,
    apiUrl: string,
    activityStatus: UIActivityStatus | undefined
): UIMessageAttachment | undefined {
    const status = activityStatus != null ? { activityStatus } : {}
    if (raw.image?.lg) return { kind: "image", uri: apiUrl + raw.image.lg, ...status }
    if (raw.audio) return { kind: "voice", uri: apiUrl + raw.audio, durationLabel: "" }
    if (raw.document) {
        const name = documentName(raw.document)
        return { kind: "document", uri: apiUrl + raw.document, ...(name != null ? { name } : {}), ...status }
    }
    return undefined
}

/**
 * Un message de la messagerie → `UIConversationMessage` — port de `CommonChat.transformMessage` :
 * le texte décodé (entités HTML) et ses adresses encodées, rien pour une pièce ; les chemins des
 * pièces préfixés de l'API (l'image en grand, `image.lg`). L'élève est l'auteur de ses messages
 * (`owner_id`), tout autre est « l'autre ». `null` : une ligne inutilisable (sans id). Une photo ou
 * un document dont le tuteur a tiré une activité porte son statut (`activityStatusOf`).
 */
export function toUIConversationMessage(
    raw: ApiMessengerMessage | null | undefined,
    selfId: string | number | null | undefined,
    apiUrl: string,
    activityStatusOf?: ActivityStatusOf
): UIConversationMessage | null {
    if (raw == null || typeof raw !== "object" || raw.id == null) return null
    const attachment = attachmentOf(raw, apiUrl, activityStatusOf?.(String(raw.id)))
    const text = attachment == null && raw.body ? cleanUrlsInBody(decode(raw.body)) : undefined
    return {
        id: String(raw.id),
        author: selfId != null && String(raw.owner_id) === String(selfId) ? "self" : "other",
        ...(raw.created_at ? { sentAt: raw.created_at } : {}),
        ...(text ? { text } : {}),
        ...(attachment != null ? { attachment } : {}),
    }
}

/**
 * Le fil lu pour un fil (`currentUser.messages[threadId]`) → les messages, du plus ancien au plus
 * récent, chacun une fois.
 *
 * La messagerie sert le plus récent d'abord et la page suivante s'ajoute au bout ; le réducteur
 * legacy ajoute aussi, au bout, la réponse d'un envoi de photo ou d'audio — une ressource NON
 * enveloppée (`MessengerApi` : `withoutWrapping`), donc `undefined` dans la liste. Le tri par date
 * et les gardes rendent l'ordre juste quel que soit le chemin d'arrivée.
 */
export function toUIConversationMessages(
    raw: unknown,
    selfId: string | number | null | undefined,
    apiUrl: string,
    activityStatusOf?: ActivityStatusOf
): UIConversationMessage[] {
    if (!Array.isArray(raw)) return []
    const byId = new Map<string, UIConversationMessage>()
    for (const row of raw as ApiMessengerMessage[]) {
        const message = toUIConversationMessage(row, selfId, apiUrl, activityStatusOf)
        if (message != null && !byId.has(message.id)) byId.set(message.id, message)
    }
    return sortBySentAt([...byId.values()])
}

/** Du plus ancien au plus récent ; un message sans date garde sa place relative (tri stable). */
export function sortBySentAt(messages: UIConversationMessage[]): UIConversationMessage[] {
    return messages
        .map((message, index) => ({ message, index, at: message.sentAt ? Date.parse(message.sentAt) : NaN }))
        .sort((a, b) => {
            if (Number.isNaN(a.at) || Number.isNaN(b.at)) return a.index - b.index
            return a.at - b.at || a.index - b.index
        })
        .map(({ message }) => message)
}

/** Ce qu'un envoi refusé laisse sur la tranche : `{ ...response.data, status }` (`apiRequest`). */
export type ApiMessengerFailure = {
    status?: number | null
    message?: unknown
    errors?: Record<string, unknown> | null
} | null

/** Ce que le fil dit d'un envoi refusé. */
export type MessengerFailure = { reason?: string; retryable: boolean }

/** Un fichier refusé sans motif : la phrase de l'ancien fil (`CommonChat.js`), mot pour mot. */
export const FILE_REFUSED = "Ce fichier n'a pas pu être envoyé. Choisis-le à nouveau."

/**
 * Un envoi refusé → ce que sa ligne dit, et si « Renvoyer » a un sens.
 *
 * L'ancien fil ouvrait la boîte « Erreur » de `useApiStatus` : le `message` du serveur puis la
 * première erreur de chaque champ (`errors`), ligne à ligne ; un fichier refusé y ajoutait sa
 * phrase. Ici la ligne du message les dit, sans doublon (le `message` de Laravel reprend souvent
 * la première erreur). Un refus du serveur (4xx) est définitif : renvoyer le même fichier échouerait
 * encore. Le reste — coupure (`status` 0 ou absent), délai (408), trop de demandes (429), session
 * à renouveler (419), panne (5xx) — se renvoie.
 */
export function messengerFailure(error: ApiMessengerFailure | undefined, isFile: boolean): MessengerFailure {
    const status = typeof error?.status === "number" ? error.status : 0
    const definitive = status >= 400 && status < 500 && ![408, 419, 429].includes(status)
    if (!definitive) return { retryable: true }

    const lines: string[] = []
    const add = (line: unknown) => {
        if (typeof line === "string" && line.trim() !== "" && !lines.includes(line.trim())) lines.push(line.trim())
    }
    for (const field of Object.values(error?.errors ?? {})) add(Array.isArray(field) ? field[0] : field)
    if (lines.length === 0) add(error?.message)
    if (lines.length === 0 && isFile) lines.push(FILE_REFUSED)
    return { retryable: false, ...(lines.length > 0 ? { reason: lines.join("\n") } : {}) }
}
